// React
import React from 'react';

// Mantine

import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';

//Refine
import { useDocumentTitle } from '@refinedev/react-router-v6';

// Components
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { CreateRowButton } from 'components/common/CreateRowButton';
import { EditRowButton } from 'components/common/EditRowButton';
import { TableAlertBanner } from 'components/common/TableAlertBanner';
import { TableProgressBar } from 'components/common/TableProgressBar';

//column
import { RoleColumns } from 'columns/Role';

// Interfaces
import { Organization, Permission, Role } from 'interfaces';

// Resource
import { useCreateRole, useUpdateRole } from 'api/resource';

// Auth
import {
  CreateMutationDataParams,
  UpdateMutationDataParams,
  createMutationData,
  updateMutationData,
} from 'api/utils/MutationDataBuilder';
import { RoleFormModal } from 'components/roles/RoleFormModal';
import { useIsAdmin, useCanModify } from 'hooks';
import { useCurrentSiteStore } from 'store/SiteStore';

interface RolesManagementProps {
  roles: Role[];
  organizations: Organization[];
  permissions: Permission[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  errorMessage?: string;
}

export const RolesManagement: React.FC<RolesManagementProps> = ({
  roles,
  organizations,
  permissions,
  isLoading,
  isError,
  isFetching,
  errorMessage,
}) => {
  const title = 'Role Management';
  useDocumentTitle('Roles | QBilling');
  const isAdmin = useIsAdmin();
  const { canModify } = useCanModify('roles');
  const { organizationID } = useCurrentSiteStore();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();

  const handleCreateRole = async ({
    values,
    table,
  }: CreateMutationDataParams<Role>) => {
    values.organizationID = isAdmin ? null : organizationID;
    // Ensure permissions array exists (RoleCreateModal populates values.permissions)
    if (!values.permissions) {
      return;
    }
    return createMutationData<Role>({
      table,
      values,
      mutation: createRole,
    });
  };

  const handleEditRole = async ({
    values,
    row,
    table,
  }: UpdateMutationDataParams<Role>) => {
    await updateMutationData<Role>({
      table,
      values,
      row,
      mutation: updateRole,
    });
  };

  const data = roles ?? [];

  const columns: MRT_ColumnDef<Role>[] = RoleColumns(
    organizations ?? [],
    permissions ?? [],
    isAdmin
  );

  const tableOptions: MRT_TableOptions<Role> = {
    data,
    columns,
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    state: {
      isLoading: isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching || isLoading,
    },
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    mantineToolbarAlertBannerProps: () => {
      return TableAlertBanner({
        isError: isError,
        message: errorMessage,
      });
    },
    initialState: {
      columnVisibility: {
        name: true,
        permissions: true,
        organizationID: isAdmin,
      },
    },
    enableFullScreenToggle: false,
    enableEditing: true,
    enableHiding: true,
    mantineLoadingOverlayProps: { visible: isLoading || isFetching },
    mantineEditRowModalProps: {
      title: 'Edit Role',
      display: 'flex',
      size: 'lg',
      overlayProps: {
        opacity: 0.15,
        blur: 5,
      },
    },
    onCreatingRowSave: handleCreateRole,
    onEditingRowSave: handleEditRole,
    renderTopToolbarCustomActions: ({ table }) => {
      return CreateRowButton<Role>({
        type: 'role',
        table,
        disabled: !canModify,
      });
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        Cell: ({ row, table }) => {
          return (
            <EditRowButton
              table={table}
              row={row}
              type="role"
              disabled={!canModify}
            />
          );
        },
      },
    },
    renderCreateRowModalContent: ({ row, table, internalEditComponents }) => {
      return (
        <RoleFormModal
          heading="Create role"
          row={row}
          table={table}
          internalEditComponents={internalEditComponents}
          permissions={permissions ?? []}
        />
      );
    },
    renderEditRowModalContent: ({ row, table, internalEditComponents }) => {
      return (
        <RoleFormModal
          heading="Edit role"
          row={row}
          table={table}
          internalEditComponents={internalEditComponents}
          permissions={permissions ?? []}
        />
      );
    },
  };

  return (
    <BillingDataGridWrapper
      title={title}
      tableOptions={tableOptions}
      dataTestID="rolesManagement"
    />
  );
};
